import React, { useState, useEffect } from 'react';
import i18next from 'i18next';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { getFormat, makeGraph } from 'lib/commonLib';
import { freqReport } from 'lib/api/freq';

import CurrentStatus from 'components/atoms/CurrentStatus';
import OutlinedButton from 'components/atoms/OutlinedButton';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import ContentsWrap from 'components/molecules/ContentsWrap';
import ChartButtonWrap from 'components/molecules/ChartButtonWrap';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const graphList = [
  {
    title: 'CTR',
    data: {
      line: [
        'CW_CLICK_RATE',
        'SR_CLICK_RATE',
        'HU_CLICK_RATE',
        'RM_CLICK_RATE',
      ],
    },
    yAxisData: {
      left: {
        RM_CLICK_RATE: true,
        HU_CLICK_RATE: true,
        SR_CLICK_RATE: true,
        CW_CLICK_RATE: true,
        isPercent: true,
      },
      right: {},
    },
    customizeData: {
      CW_CLICK_RATE: {
        tooltipFormat: '0.00',
        isPercent: true,
      },
      SR_CLICK_RATE: {
        tooltipFormat: '0.00',
        isPercent: true,
      },
      HU_CLICK_RATE: {
        tooltipFormat: '0.00',
        isPercent: true,
      },
      RM_CLICK_RATE: {
        tooltipFormat: '0.00',
        isPercent: true,
      },
    },
  },
  {
    title: i18next.t('세션 ROAS'),
    data: {
      line: [
        'CW_SESSION_FREQ_ROAS',
        'SR_SESSION_FREQ_ROAS',
        'HU_SESSION_FREQ_ROAS',
        'RM_SESSION_FREQ_ROAS',
      ],
    },
    yAxisData: {
      left: {
        CW_SESSION_FREQ_ROAS: true,
        SR_SESSION_FREQ_ROAS: true,
        HU_SESSION_FREQ_ROAS: true,
        RM_SESSION_FREQ_ROAS: true,
        isPercent: true,
      },
      right: {},
    },
    customizeData: {
      CW_SESSION_FREQ_ROAS: {
        isPercent: true,
      },
      SR_SESSION_FREQ_ROAS: {
        isPercent: true,
      },
      HU_SESSION_FREQ_ROAS: {
        isPercent: true,
      },
      RM_SESSION_FREQ_ROAS: {
        isPercent: true,
      },
    },
  },
  {
    title: i18next.t('총 ROAS'),
    data: {
      line: ['CW_FREQ_ROAS', 'SR_FREQ_ROAS', 'HU_FREQ_ROAS', 'RM_FREQ_ROAS'],
    },
    yAxisData: {
      left: {
        CW_FREQ_ROAS: true,
        SR_FREQ_ROAS: true,
        HU_FREQ_ROAS: true,
        RM_FREQ_ROAS: true,
        isPercent: true,
      },
      right: {},
    },
    customizeData: {
      CW_FREQ_ROAS: {
        isPercent: true,
      },
      SR_FREQ_ROAS: {
        isPercent: true,
      },
      HU_FREQ_ROAS: {
        isPercent: true,
      },
      RM_FREQ_ROAS: {
        isPercent: true,
      },
    },
  },
  {
    title: i18next.t('전환율'),
    data: {
      line: ['CW_CONV_RATE', 'SR_CONV_RATE', 'HU_CONV_RATE', 'RM_CONV_RATE'],
    },
    yAxisData: {
      left: {
        CW_CONV_RATE: true,
        SR_CONV_RATE: true,
        HU_CONV_RATE: true,
        RM_CONV_RATE: true,
        isPercent: true,
      },
      right: {},
    },
    customizeData: {
      CW_CONV_RATE: {
        isPercent: true,
      },
      SR_CONV_RATE: {
        isPercent: true,
      },
      HU_CONV_RATE: {
        isPercent: true,
      },
      RM_CONV_RATE: {
        isPercent: true,
      },
    },
  },
  {
    title: 'ECPM',
    data: {
      line: ['CW_ECPM', 'SR_ECPM', 'HU_ECPM', 'RM_ECPM'],
    },
    yAxisData: {
      left: {
        CW_ECPM: true,
        SR_ECPM: true,
        HU_ECPM: true,
        RM_ECPM: true,
      },
      right: {},
    },
  },
  {
    title: i18next.t('노출'),
    data: {
      line: [
        'CW_PAR_EPRS_CNT',
        'SR_PAR_EPRS_CNT',
        'HU_PAR_EPRS_CNT',
        'RM_PAR_EPRS_CNT',
      ],
    },
    yAxisData: {
      left: {
        CW_PAR_EPRS_CNT: true,
        SR_PAR_EPRS_CNT: true,
        HU_PAR_EPRS_CNT: true,
        RM_PAR_EPRS_CNT: true,
      },
      right: {},
    },
  },
  {
    title: i18next.t('지출금액'),
    data: {
      line: [
        'CW_ADVRTS_AMT',
        'SR_ADVRTS_AMT',
        'HU_ADVRTS_AMT',
        'RM_ADVRTS_AMT',
      ],
    },
    yAxisData: {
      left: {
        CW_ADVRTS_AMT: true,
        SR_ADVRTS_AMT: true,
        HU_ADVRTS_AMT: true,
        RM_ADVRTS_AMT: true,
      },
      right: {},
    },
  },
];

const FreqStatisticsCrossGraph = (props) => {
  const { t } = useTranslation();
  const { locale } = useSelector(({ locale }) => ({
    locale: locale.locale,
  }));
  const { params } = props;

  // 공통 필터에서 초기값 받아왔는지 확인
  const [error, setError] = useState(false);
  const [infoMessage, setInfoMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(false);
  const [primitiveData, setPrimitiveData] = useState([]);
  const [curGraphComponent, setCurGraphComponent] = useState([]);

  /**
   * 버튼 클릭하여 그래프 데이터 변경
   * graphData json, index
   */
  const handleChangeGraph =
    (
      {
        title,
        data,
        yAxisData,
        customizeData: customize,
        isPercent = false,
        referenceLine,
      },
      index
    ) =>
    (e) => {
      if (typeof data !== 'undefined') {
        const {
          graphData,
          dataKeys,
          customizeData,
          keyLabel,
          stackId,
          referenceData,
        } = makeGraph({ data, customize, primitiveData, referenceLine });

        const result = (
          <MultiplexChart
            width="100%"
            xAxisTick={xAxisTick}
            makeLabel={makeLabel(false)}
            data={graphData}
            dataKeys={dataKeys}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={stackId.length !== 0 ? stackId.join('$') : null}
            yAxisData={yAxisData}
            legend
            title={title}
            isPercent={isPercent}
            key={title}
            brush={{ startIndex: 0, endIndex: 40 }}
            referenceLine={referenceData}
          />
        );

        setCurGraphComponent(result);
        setSelectedIdx(index);
      }
    };

  useEffect(() => {
    // 렌더링 후에 실행됨
    // params setting(필터링 혹은 초기 param setting) > 렌더링 > 초기화 값 초기화
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps,
  }, [params]);

  useEffect(() => {
    // 렌더링 후에 실행됨
    // params setting(필터링 혹은 초기 param setting) > 렌더링 > 초기화 값 초기화
    if (primitiveData.length !== 0) {
      handleChangeGraph(graphList[0], 0)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps,
  }, [primitiveData]);

  const getData = () => {
    const { sDate, eDate } = params;
    const format = getFormat(locale);

    setInfoMessage(
      t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      })
    );
    setLoading(true);

    freqReport({ ...params })
      .then(async (response) => {
        const { data } = response.data;
        setPrimitiveData(data);
        setLoading(false);
        setError(false);
      })
      .catch((error) => {
        setLoading(false);
        setError(true);
      });
  };

  // 월 일 or 일 반환
  const makeLabel = (onlyDay) => (originalDate) => {
    let color = '';

    return <span style={{ color }}>{originalDate}</span>;
  };

  const xAxisTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;

    let fill = '';

    return (
      <text x={x} y={y + 10} fill={fill} textAnchor="middle">
        {value}
      </text>
    );
  };

  /**
   * 새로고침
   */
  const handleRefesh = () => {
    this.getData();
  };

  return (
    <SwitchingArea
      title={
        <>
          <InfoTitleArea
            title={t('프리퀀시별 그래프')}
            tooltipTitle={infoMessage}
            iconType="fainfoCircle"
            iconColor="graph"
          />
        </>
      }
      handleRefesh={handleRefesh}
    >
      {loading || error ? (
        <CurrentStatus type={loading ? 'loading' : error && 'error'} />
      ) : (
        <>
          <ChartButtonWrap>
            {graphList.map((item, index) => {
              return (
                <OutlinedButton
                  key={item.title}
                  onClick={handleChangeGraph(item, index)}
                  selected={selectedIdx === index}
                  disabled={!Boolean(primitiveData)}
                >
                  {item.title}
                </OutlinedButton>
              );
            })}
          </ChartButtonWrap>
          <ContentsWrap contentsCount={1}>{curGraphComponent}</ContentsWrap>
        </>
      )}
    </SwitchingArea>
  );
};

export default FreqStatisticsCrossGraph;
